/**
 * @fileoverview Vendor Verification Status Hook
 * 
 * Provides a reusable hook to check if the current vendor is verified.
 * A vendor is considered verified if their verification_level is 
 * 'basic', 'enhanced', or 'premium'.
 * 
 * @module useVendorVerificationStatus
 */

import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/lib/auth';

export type VerificationLevel = 'none' | 'basic' | 'enhanced' | 'premium';

interface VendorVerificationStatus {
  /** Whether the vendor has any level of verification (basic, enhanced, or premium) */
  isVerified: boolean;
  /** The current verification level */
  verificationLevel: VerificationLevel;
  /** Whether the status is still being loaded */
  loading: boolean;
  /** Error message if fetch failed */
  error: string | null;
}

/**
 * Hook to check the current vendor's verification status
 * 
 * @returns Object with isVerified boolean, verificationLevel, loading state, and error
 * 
 * @example
 * ```typescript
 * const { isVerified, verificationLevel, loading } = useVendorVerificationStatus();
 * 
 * if (!loading && !isVerified) {
 *   // Show restricted access UI
 * }
 * ```
 */
export const useVendorVerificationStatus = (): VendorVerificationStatus => {
  const { user } = useAuth();
  const [isVerified, setIsVerified] = useState(false);
  const [verificationLevel, setVerificationLevel] = useState<VerificationLevel>('none');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchVerificationStatus = async () => {
      // Non-vendor users are not subject to verification
      if (!user || user.role !== 'vendor') {
        setLoading(false);
        setIsVerified(true); // Clients/admins can access everything
        return;
      }

      try {
        setLoading(true);
        
        const { data, error: fetchError } = await supabase
          .from('vendor_profiles_enhanced')
          .select('is_verified, verification_level')
          .eq('user_id', user.id)
          .maybeSingle();

        if (fetchError) throw fetchError;

        if (data) {
          const level = (data.verification_level as VerificationLevel) || 'none';
          setVerificationLevel(level);
          
          // Vendor is verified if they have basic, enhanced, or premium status
          const verified = data.is_verified === true && 
            ['basic', 'enhanced', 'premium'].includes(level);
          setIsVerified(verified);
        } else {
          // No profile found - treat as unverified
          setIsVerified(false);
          setVerificationLevel('none');
        }

        setError(null);
      } catch (err) {
        console.error('Error fetching verification status:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch verification status');
        // Default to unverified on error for safety
        setIsVerified(false);
        setVerificationLevel('none');
      } finally {
        setLoading(false);
      }
    };

    fetchVerificationStatus();
  }, [user]);

  return { isVerified, verificationLevel, loading, error };
};
